import React, { useEffect, useCallback, useRef, useState } from "react";
import type { Grid, Token, Direction } from "../types.ts";
import { drawGrid } from "../canvas/grid-renderer.ts";

interface GameBoardProps {
  grid: Grid;
  tokens: Token[];
  disabled: boolean;
  onMove: (dir: Direction) => void;
}

const SLIDE_DURATION = 180; // ms

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function GameBoard({ grid, tokens, disabled, onMove }: GameBoardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prevTokensRef = useRef<Token[]>(tokens);
  const animFrameRef = useRef<number>(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Draw function (shared between static and animated draws)
  const renderFrame = useCallback(
    (displayTokens: Token[]) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      drawGrid(ctx, grid, displayTokens, rect.width, rect.height);
    },
    [grid]
  );

  // Static redraw when not animating
  useEffect(() => {
    if (isAnimating) return;
    renderFrame(tokens);
  }, [grid, tokens, isAnimating, renderFrame]);

  // Resize handler
  useEffect(() => {
    if (isAnimating) return;
    const handleResize = () => renderFrame(tokens);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [tokens, isAnimating, renderFrame]);

  // Animate token sliding when positions change
  useEffect(() => {
    const prev = prevTokensRef.current;
    prevTokensRef.current = tokens;

    // Check if any alive token moved
    const hasMovement = tokens.some((t, i) => {
      const p = prev[i];
      return p && !p.lost && !t.lost &&
        (t.pos.row !== p.pos.row || t.pos.col !== p.pos.col);
    });
    if (!hasMovement) return;

    setIsAnimating(true);
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / SLIDE_DURATION, 1);
      const eased = easeOutCubic(t);

      const lerpedTokens: Token[] = tokens.map((token, i) => {
        const prevToken = prev[i];
        if (!prevToken || prevToken.lost || token.lost) return token;
        return {
          ...token,
          pos: {
            row: prevToken.pos.row + (token.pos.row - prevToken.pos.row) * eased,
            col: prevToken.pos.col + (token.pos.col - prevToken.pos.col) * eased,
          },
        };
      });

      renderFrame(lerpedTokens);

      if (t < 1) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [tokens, renderFrame]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Keyboard input
  useEffect(() => {
    if (disabled || isAnimating) return;
    const handler = (e: KeyboardEvent) => {
      const dirMap: Record<string, Direction> = {
        ArrowUp: "up",
        ArrowDown: "down",
        ArrowLeft: "left",
        ArrowRight: "right",
        w: "up",
        s: "down",
        a: "left",
        d: "right",
      };
      const dir = dirMap[e.key];
      if (dir) {
        e.preventDefault();
        onMove(dir);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [disabled, isAnimating, onMove]);

  // Touch swipe input
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      touchStart.current = { x: touch.clientX, y: touch.clientY };
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || isAnimating || !touchStart.current) return;
      const touch = e.changedTouches[0];
      if (!touch) return;

      const dx = touch.clientX - touchStart.current.x;
      const dy = touch.clientY - touchStart.current.y;
      const threshold = 30;

      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;

      let dir: Direction;
      if (Math.abs(dx) > Math.abs(dy)) {
        dir = dx > 0 ? "right" : "left";
      } else {
        dir = dy > 0 ? "down" : "up";
      }

      onMove(dir);
      touchStart.current = null;
    },
    [disabled, isAnimating, onMove]
  );

  return (
    <canvas
      ref={canvasRef}
      className="w-full aspect-square max-w-[560px] touch-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    />
  );
}
